import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { ResponseError } from '@ltvco/refresh-lib/utils';
import { useServiceToken, useSnackbar } from '@ltvco/refresh-lib/v1';
import { useMutation } from '@ltvco/refresh-lib/vendors';
import { useContext } from 'react';

import { LLMRequestItem } from 'components/ChatBV/types';

interface WebSearchBotResponse {
  context_id: string;
  data: {
    answer?: string;
  };
  date: string;
  response_id: string;
}

export const useLLMWebSearch = () => {
  const useLLMWebSearchMaxRetries = 2;
  const serviceTokenResult = useServiceToken('llm') as {
    data?: { token?: string };
  };
  const tokenData = serviceTokenResult.data;
  const { enqueueSnackbar } = useSnackbar();
  const { logError } = useContext(AppConfig);

  const handleWebSearchError = (error: ResponseError) => {
    if (error.response?.status === 429) {
      logError('web-search: rate limit error', error);
    } else {
      logError('web-search: generation failed', error);
    }
    enqueueSnackbar(
      'Something went wrong while generating a response. Please try again.',
      {
        variant: 'error',
        autoHideDuration: 3000,
      }
    );
  };

  const fetchWebSearchResponse = async ({
    prompt,
  }: LLMRequestItem): Promise<WebSearchBotResponse | null> => {
    if (!tokenData?.token) {
      throw new Error('Missing LLM service token');
    }

    const response = await fetch('/ms/insight/websearchbot', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenData.token}`,
      },
      credentials: 'include',
      body: JSON.stringify({
        ...(prompt && { prompt }),
      }),
    });

    if (!response.ok) {
      const error: any = new Error(`HTTP error! status: ${response.status}`);
      error.response = { status: response.status };
      throw error;
    }

    return await response.json();
  };

  const webSearchMutation = useMutation(fetchWebSearchResponse, {
    retry: (failureCount, error: ResponseError) => {
      if (error?.response?.status === 429) {
        return false;
      }
      const maxRetries = useLLMWebSearchMaxRetries || 1;
      return failureCount < maxRetries;
    },
    onError: (error: ResponseError) => {
      handleWebSearchError(error);
    },
  });

  return webSearchMutation;
};
